import { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categoryMovies, categoryInfo } from '@/mocks/category';

const tabKeys = ['movie', 'tv', 'variety', 'anime'] as const;
const tabLabels: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  variety: '综艺',
  anime: '动漫',
};

function ScrollToTop() {
  const { slug } = useParams();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);
  return null;
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<string>(slug || 'movie');

  useEffect(() => {
    if (slug && tabKeys.includes(slug as typeof tabKeys[number])) {
      setActiveTab(slug);
    }
  }, [slug]);

  if (!slug || !tabKeys.includes(slug as typeof tabKeys[number])) {
    return <Navigate to="/category/movie" replace />;
  }

  const info = categoryInfo[slug] || categoryInfo.movie;
  const movies = categoryMovies[slug] || [];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb + page header */}
        <section className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-3 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">{tabLabels[slug]}</span>
            </nav>

            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
              {info.name}大全
            </h1>
            <p className="text-sm text-foreground-600">{info.desc}</p>
          </div>
        </section>

        {/* Tab switcher */}
        <section className="sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 py-3 overflow-x-auto">
              {tabKeys.map((key) => (
                <Link
                  key={key}
                  to={`/category/${key}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeTab === key
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                  }`}
                >
                  {tabLabels[key]}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Movie grid */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-foreground-500">
                共 <strong className="text-foreground-800">{movies.length}</strong> 部{tabLabels[slug]}资源
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {movies.map((movie) => (
                <Link
                  key={movie.id}
                  to={movie.link}
                  className="group cursor-pointer"
                >
                  <div className="relative aspect-[2/3] rounded-lg overflow-hidden mb-3 bg-background-200">
                    <img
                      src={movie.image}
                      alt={movie.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-2 right-2 flex items-center gap-0.5 bg-black/70 backdrop-blur-sm rounded-md px-2 py-0.5">
                      <i className="ri-star-fill text-accent-500 text-xs"></i>
                      <span className="text-white text-xs font-medium">{movie.rating}</span>
                    </div>
                    <div className="absolute top-2 left-2">
                      <span className="text-xs px-2 py-0.5 rounded-md bg-secondary-500 text-white">{movie.region}</span>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                      <p className="text-white text-xs leading-relaxed line-clamp-3">{movie.desc}</p>
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
                    {movie.title}
                  </h3>
                  <p className="text-xs text-foreground-500">{movie.category} · {movie.year}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}